import { msalConfig } from "../authConfig";
import { fetchWithAuth } from "./fetchWithAuth";
import { PublicClientApplication, IPublicClientApplication } from "@azure/msal-browser";
import { ReleaseVersion, ReleaseVersionIssue, TicketCommitsResponse, FixVersionSummaryByCategory, FixVersionIssuesResponse, ConfigHealthResponse } from "./Types";


export const msalInstance = new PublicClientApplication(msalConfig);

export async function ensureMsalInitialized() {
  await msalInstance.initialize(); // initialize() is idempotent
}

const BASE_URL = "https://outthink-opsnest-api.ot-services-prd-usa.net";
// const BASE_URL = "http://localhost:9000"


export const GithubAPI = {
  baseUrl: BASE_URL,
  getRepositories: (branch: string, since: string, until: string) =>
    `${BASE_URL}/github/repositories?branch=${branch}&org_name=outthinkthreats&since=${since}&until=${until}`,
  // `${BASE_URL}/github/repositories?branch=${branch}&since=${since}&until=${until}`,
  compareBranches: (repository: string, baseBranch: string, headBranch: string) =>
    `${BASE_URL}/github/compare/${repository}/${baseBranch}/${headBranch}`,
  releaseDrops: (sourceBranch: string, targetBranch: string, since: string, until: string) =>
    `${BASE_URL}/github/release-drop?org_name=outthinkthreats&source_branch=${sourceBranch}&target_branch=${targetBranch}&since=${since}&until=${until}`,
};

export const KubernetesAPI = {
  getNamespaces: () => `${BASE_URL}/kubernetes/namespaces`,
  getPods: () => `${BASE_URL}/kubernetes/pods`,
  getPodsByNamespace: (namespace: string) => `${BASE_URL}/kubernetes/pods/${namespace}`,
  getDeployments: () => `${BASE_URL}/kubernetes/deployments`,
  getDeploymentsByNamespace: (namespace: string) => `${BASE_URL}/kubernetes/deployments/${namespace}`,
  restartDeployment: (namespace: string, name: string) => 
    `${BASE_URL}/kubernetes/deployments/${namespace}/${name}/restart`,
  restartPod: (namespace: string, podName: string) => 
    `${BASE_URL}/kubernetes/deployment/${namespace}/${podName}/restart`,
  describePod: (namespace: string, podName: string) => 
    `${BASE_URL}/kubernetes/pods/${namespace}/${podName}/describe`,
  getServices: () => `${BASE_URL}/kubernetes/services`,
  getServicesByNamespace: (namespace: string) => `${BASE_URL}/kubernetes/services/${namespace}`,
  // Maintenance endpoints
  getPodsForMaintenance: () => `${BASE_URL}/kubernetes/pods/maintenance`,
  getNodesForMaintenance: () => `${BASE_URL}/kubernetes/nodes`
};

export const AzureAPI = {
  getAppRegistrationSecrets: (from: string, until: string) =>
    `${BASE_URL}/azure/expiring-app-secrets-range?from=${encodeURIComponent(from)}&until=${encodeURIComponent(until)}`,
  sendExpiringSecretNotification: (appName: string, secretName: string, expiryDate: string) =>
    `${BASE_URL}/azure/send-expiring-secret-notification?app_name=${encodeURIComponent(appName)}&secret_name=${encodeURIComponent(secretName)}&expiry_date=${encodeURIComponent(expiryDate)}`
};


export async function GetReleaseVersions(
    msalInstance: IPublicClientApplication,
    account: any,
    projectKey: string
): Promise<ReleaseVersion[]> {
    await ensureMsalInitialized();
    if (!account) throw new Error("No account found. Please sign in.");

    const url = `${GithubAPI.baseUrl}/jira/project/${projectKey}/fix-versions`;
    const response = await fetchWithAuth(url, msalInstance, account);

    if (!response.ok) {
        throw new Error(`API returned ${response.status}: ${await response.text()}`);
    }
    return await response.json();
}

export async function GetReleaseVersionIssues(
  msalInstance: any,
  account: any,
  projectKey: string,
  fixVersionId: string
): Promise<FixVersionIssuesResponse> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");
  const url = `${GithubAPI.baseUrl}/jira/project/${projectKey}/fix-versions/${fixVersionId}/issues`;
  const response = await fetchWithAuth(url, msalInstance, account, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error(`Failed to fetch issues: ${response.status}`);
  return response.json();
}

export async function GetTicketCommits(ticketNumber: string, msalInstance: any, account: any): Promise<TicketCommitsResponse> {
    await ensureMsalInitialized();
    if (!account) throw new Error("No account found. Please sign in.");
    const url = `${GithubAPI.baseUrl}/github/tickets/${ticketNumber}/commits`;
    const response = await fetchWithAuth(url, msalInstance, account, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) throw new Error("Failed to fetch ticket commits");
    return await response.json();
}

export async function GetCommitFiles(
  repoName: string,
  sha: string,
  msalInstance: any,
  account: any
): Promise<any> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");
  const url = `${GithubAPI.baseUrl}/github/repos/${repoName}/commits/${sha}/files`;
  const response = await fetchWithAuth(url, msalInstance, account, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error(`Failed to fetch commit files: ${response.status}`);
  return response.json();
}

export async function GetPromotionPlan(
  msalInstance: any,
  account: any,
  projectKey: string,
  fixVersionId: string,
  promotionTarget: string
): Promise<any> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");
  const params = new URLSearchParams({
    project_key: projectKey,
    fix_version_id: fixVersionId,
    promotion_target: promotionTarget
  }).toString();
  const url = `${GithubAPI.baseUrl}/github/promotion/plan?${params}`;
  const response = await fetchWithAuth(url, msalInstance, account, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!response.ok) throw new Error(`Failed to fetch promotion plan: ${response.status}`);
  return response.json();
}

export async function ExecutePromotion(
  msalInstance: any,
  account: any,
  body: {
    org_name: string;
    promotion_target: string;
    project_key: string;
    fix_version_id: string;
    fix_version_name: string;
    strategy: string;
    repo_selections: Array<{
      repo: string;
      source_branch: string;
      target_branch: string;
      selected_commits: Array<{
        repo_name: string;
        branch_name: string;
        jira_ticket: string;
        commit_sha: string;
        message: string;
        author: string;
        committed_at: string;
        category: string;
      }>;
    }>;
  }
): Promise<any> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");
  const url = `${GithubAPI.baseUrl}/github/promotion/execute`;
  const response = await fetchWithAuth(url, msalInstance, account, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!response.ok) throw new Error(`Failed to execute promotion: ${response.status}`);
  return response.json();
}

export async function NotifyTeams(
    msalInstance: IPublicClientApplication,
    account: any,
    payload: { message: string; promotion_target: string; fix_version_name: string }
): Promise<void> {
    await ensureMsalInitialized();
    if (!account) throw new Error("No account found. Please sign in.");

    const url = `${GithubAPI.baseUrl}/github/promotion/notify-teams`;
    const response = await fetchWithAuth(url, msalInstance, account, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(`API returned ${response.status}: ${await response.text()}`);
    }
}

export async function SubmitConfigEntry(
  msalInstance: IPublicClientApplication,
  account: any,
  payload: {
    environment: string;
    region?: string;
    entries: Array<{
      key: string;
      value_type: string;
      value?: string;
      vault_uri?: string;
      secret_name?: string;
    }>;
  }
): Promise<{ result: string; request_id: string; conflicts: any[] }> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");

  const url = `${GithubAPI.baseUrl}/config/submit`;
  const response = await fetchWithAuth(url, msalInstance, account, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || `Failed to submit config entry: ${response.status}`);
  }
  return response.json();
}

export async function GetConfigRequestStatus(
  msalInstance: IPublicClientApplication,
  account: any,
  requestId: string
): Promise<{
  request_id: string;
  status: string;
  environment: string;
  submitted_by: string;
  submitted_at: string;
  entries: Array<{
    key: string;
    value_type: string;
    value?: string;
    vault_uri?: string;
    secret_name?: string;
  }>;
}> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");

  const url = `${GithubAPI.baseUrl}/config/requests/${requestId}/status`;
  const response = await fetchWithAuth(url, msalInstance, account);

  if (!response.ok) {
    throw new Error(`Failed to get request status: ${response.status}`);
  }
  return response.json();
}

export async function GetConfigAuditLog(
  msalInstance: IPublicClientApplication,
  account: any,
): Promise<{
  entries: Array<{
    id: string;
    request_id: string | null;
    event_type: string;
    environment: string;
    region: string | null;
    key: string;
    value_type: string | null;
    actor: string;
    occurred_at: string;
    status: string | null;
    error: string | null;
  }>;
}> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");

  const url = `${GithubAPI.baseUrl}/config/audit`;
  const response = await fetchWithAuth(url, msalInstance, account);

  if (!response.ok) {
    throw new Error(`Failed to fetch audit log: ${response.status}`);
  }
  return response.json();
}
export async function GetPendingConfigRequests(
  msalInstance: IPublicClientApplication,
  account: any,
): Promise<any[]> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");

  const url = `${GithubAPI.baseUrl}/config/pending`;
  const response = await fetchWithAuth(url, msalInstance, account);
  if (!response.ok) throw new Error(`Failed to fetch pending requests: ${response.status}`);
  return response.json();
}

export async function ApproveConfigRequest(
  msalInstance: IPublicClientApplication,
  account: any,
  requestId: string,
): Promise<void> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");

  const url = `${GithubAPI.baseUrl}/config/requests/${requestId}/approve`;
  const response = await fetchWithAuth(url, msalInstance, account, {
    method: "POST",
  });
  if (!response.ok) throw new Error(`Failed to approve request: ${response.status}`);
}

export async function RejectConfigRequest(
  msalInstance: IPublicClientApplication,
  account: any,
  requestId: string,
  reason?: string,
): Promise<void> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");

  const url = `${GithubAPI.baseUrl}/config/requests/${requestId}/reject`;
  const response = await fetchWithAuth(url, msalInstance, account, {
    method: "POST",
    body: JSON.stringify({ reason: reason || null }),
  });
  if (!response.ok) throw new Error(`Failed to reject request: ${response.status}`);
}

export async function GetConfigHealth(
  msalInstance: IPublicClientApplication,
  account: any,
  refresh: boolean = false,
): Promise<ConfigHealthResponse> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");

  const url = `${GithubAPI.baseUrl}/config/health${refresh ? "?refresh=true" : ""}`;
  const response = await fetchWithAuth(url, msalInstance, account, {
    method: "GET",
  });
  if (!response.ok) throw new Error(`Failed to fetch config health: ${response.status}`);
  return response.json();
}

export async function CheckConfigKeys(
  msalInstance: IPublicClientApplication,
  account: any,
  payload: { environment: string; region?: string; keys: string[] },
): Promise<{ existing_keys: string[] }> {
  await ensureMsalInitialized();
  if (!account) throw new Error("No account found. Please sign in.");

  const url = `${GithubAPI.baseUrl}/config/check-keys`;
  const response = await fetchWithAuth(url, msalInstance, account, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`Failed to check keys: ${response.status}`);
  return response.json();
}

export { fetchWithAuth };
